import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Avatar,
  Container,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { getAdminAccounts, createAdminAccount, updateAdminAccount } from "../../../Services/AdminUsersApi";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CodeIcon from "@mui/icons-material/Code";
import EventIcon from "@mui/icons-material/Event";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

const UserspageMain = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAdminAccounts();
      console.log("Admin Accounts Response:", response);

      if (response?.data?.accounts && Array.isArray(response.data.accounts)) {
        setUsers(response.data.accounts);
      } else {
        setUsers([]);
        console.warn("Invalid data format:", response);
      }
    } catch (error) {
      console.error("Error fetching admin accounts:", error);
      setError("فشل في جلب بيانات المستخدمين");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createAdminAccount(formData);
      alert("✅ تم إنشاء الحساب بنجاح!");
      handleCloseDialog();
      fetchUsers(); // إعادة تحميل القائمة
    } catch (error) {
      console.error("Error creating admin account:", error);
      alert("❌ فشل في إنشاء الحساب. تحقق من البيانات.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditDialog = (user) => {
    setEditingUserId(user.id);
    setEditFormData({
      name: user.name || "",
      email: user.email || "",
      password: "", // كلمة المرور فارغة (اختياري)
      phone: user.phone || "",
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUserId(null);
    setEditFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // إرسال البيانات فقط إذا كانت موجودة
      const dataToUpdate = {};
      if (editFormData.name) dataToUpdate.name = editFormData.name;
      if (editFormData.email) dataToUpdate.email = editFormData.email;
      if (editFormData.phone) dataToUpdate.phone = editFormData.phone;
      if (editFormData.password) dataToUpdate.password = editFormData.password;

      await updateAdminAccount(editingUserId, dataToUpdate);
      alert("✅ تم تعديل الحساب بنجاح!");
      handleCloseEditDialog();
      fetchUsers(); // إعادة تحميل القائمة
    } catch (error) {
      console.error("Error updating admin account:", error);
      alert("❌ فشل في تعديل الحساب. تحقق من البيانات.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    return status === 1 ? "success" : "error";
  };

  const getStatusText = (status) => {
    return status === 1 ? "نشط" : "غير نشط";
  };

  const getShortEmail = (email) => {
    if (!email) return "";
    const atIndex = email.indexOf("@");
    return atIndex > 0 ? email.substring(0, atIndex) : email;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل البيانات...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ fontSize: "1.1rem", py: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: "white",
                  color: "#667eea",
                  width: 50,
                  height: 50,
                }}
              >
                <AdminPanelSettingsIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  قائمة مستخدمي النظام
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255,255,255,0.9)" }}
                >
                  إدارة حسابات الأدمن والمستخدمين
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${users.length} مستخدم`}
                sx={{
                  bgcolor: "white",
                  color: "#667eea",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  px: 2,
                  py: 3,
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleOpenDialog}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                إضافة حساب جديد
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      {users.length > 0 ? (
        <Card elevation={3} sx={{ borderRadius: 2 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    الاسم
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    البريد الإلكتروني
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    رقم الهاتف
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    كود الوسيط
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    الحالة
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    تاريخ التسجيل
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                    إجراءات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      "&:hover": {
                        bgcolor: user.active === 1 ? "#e8f5e9" : "#fff3e0",
                        transition: "all 0.2s",
                      },
                      bgcolor: user.active === 1 ? "transparent" : "#fffde7",
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: user.active === 1 ? "#4caf50" : "#ff9800",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {user.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <EmailIcon sx={{ color: "#666", fontSize: 18 }} />
                        <Typography variant="body2">
                          {getShortEmail(user.email)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <PhoneIcon sx={{ color: "#666", fontSize: 18 }} />
                        <Chip
                          label={user.phone || "لا يوجد"}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CodeIcon sx={{ color: "#666", fontSize: 18 }} />
                        {user.broker_code ? (
                          <Chip
                            label={user.broker_code}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontFamily: "monospace" }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            لا يوجد
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          user.active === 1 ? (
                            <CheckCircleIcon />
                          ) : (
                            <ErrorOutlineIcon />
                          )
                        }
                        label={getStatusText(user.active)}
                        color={getStatusColor(user.active)}
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <EventIcon sx={{ color: "#666", fontSize: 18 }} />
                        <Typography variant="body2">
                          {new Date(user.created_at).toLocaleDateString("ar-EG")}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="تعديل الحساب">
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() => handleOpenEditDialog(user)}
                          sx={{
                            bgcolor: "#f3e5f5",
                            "&:hover": { bgcolor: "#e1bee7" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <AdminPanelSettingsIcon
            sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            لا توجد حسابات مستخدمين
          </Typography>
        </Box>
      )}

      {/* Dialog إضافة حساب جديد */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#667eea', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#667eea', width: 40, height: 40 }}>
              <AddCircleIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                إضافة حساب أدمن جديد
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                أدخل بيانات المستخدم الجديد
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ color: '#667eea' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      الاسم الكامل
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="أدخل الاسم الكامل"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmailIcon sx={{ color: '#667eea' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      البريد الإلكتروني
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@domain.com"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon sx={{ color: '#667eea' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      رقم الهاتف
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="01XXXXXXXXX"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CodeIcon sx={{ color: '#667eea' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      كلمة المرور
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="أدخل كلمة مرور قوية"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                  <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                    يجب أن تحتوي على أحرف وأرقام ورموز
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            size="large"
            disabled={submitting}
            sx={{ minWidth: 100 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddCircleIcon />}
            sx={{ 
              minWidth: 120,
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5568d3 30%, #6a3f8f 90%)',
              }
            }}
          >
            {submitting ? 'جاري الإنشاء...' : 'إنشاء الحساب'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog تعديل حساب */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#9c27b0', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#9c27b0', width: 40, height: 40 }}>
              <EditIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                تعديل حساب الأدمن
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                قم بتعديل البيانات المطلوبة
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseEditDialog}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box component="form" onSubmit={handleEditSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ color: '#9c27b0' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      الاسم الكامل
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    placeholder="أدخل الاسم الكامل"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmailIcon sx={{ color: '#9c27b0' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      البريد الإلكتروني
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    placeholder="example@domain.com"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon sx={{ color: '#9c27b0' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      رقم الهاتف
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditChange}
                    placeholder="01XXXXXXXXX"
                    required
                    sx={{ bgcolor: 'white' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#fff3e0', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CodeIcon sx={{ color: '#ff9800' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      كلمة المرور الجديدة
                    </Typography>
                    <Chip label="اختياري" size="small" color="warning" />
                  </Box>
                  <TextField
                    fullWidth
                    name="password"
                    type="password"
                    value={editFormData.password}
                    onChange={handleEditChange}
                    placeholder="اتركه فارغاً إذا لا تريد تغييره"
                    sx={{ bgcolor: 'white' }}
                  />
                  <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                    اترك الحقل فارغاً إذا كنت لا تريد تغيير كلمة المرور
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCloseEditDialog}
            variant="outlined"
            size="large"
            disabled={submitting}
            sx={{ minWidth: 100 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
            sx={{ 
              minWidth: 120,
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
              }
            }}
          >
            {submitting ? 'جاري التعديل...' : 'حفظ التعديلات'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserspageMain;
